/**
 * ActivityRewind Component
 * 
 * Video-player-like interface for reviewing student activity screenshots.
 * Features:
 * - Timeline scrubber with color-coded event markers
 * - Playback controls (play, pause, speed, step)
 * - Screenshot display area
 * - Activity metadata panel
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { screenshots as screenshotsApi, activity as activityApi } from '../services/api';

// Styles
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: 'var(--bg-secondary)',
    color: 'var(--text-primary)',
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
    borderRadius: '12px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-primary)',
    backgroundColor: 'var(--bg-tertiary)',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
  },
  recordingBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    backgroundColor: 'var(--btn-danger-bg)',
    borderRadius: '20px',
    fontSize: '12px',
    color: 'var(--accent-danger)',
  },
  recordingDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'var(--accent-danger)',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  closeButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    border: '1px solid var(--border-primary)',
    borderRadius: '6px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  screenshotArea: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'var(--bg-primary)',
    padding: '20px',
    position: 'relative',
  },
  screenshot: {
    maxWidth: '100%',
    maxHeight: '100%',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg)',
  },
  noScreenshot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    color: 'var(--text-muted)',
  },
  metadataPanel: {
    width: '320px',
    backgroundColor: 'var(--bg-tertiary)',
    borderLeft: '1px solid var(--border-primary)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  metadataHeader: {
    padding: '16px',
    borderBottom: '1px solid var(--border-primary)',
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-secondary)',
  },
  eventsList: {
    flex: 1,
    overflow: 'auto',
    padding: '8px',
  },
  eventItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '10px 12px',
    marginBottom: '4px',
    backgroundColor: 'var(--bg-secondary)',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  eventItemActive: {
    backgroundColor: 'var(--bg-hover)',
    borderLeft: '3px solid var(--accent-purple)',
  },
  eventDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    marginTop: '4px',
    flexShrink: 0,
  },
  eventContent: {
    flex: 1,
    minWidth: 0,
  },
  eventTime: {
    color: 'var(--text-muted)',
    fontSize: '11px',
    marginBottom: '2px',
  },
  eventText: {
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
  },
  controlsArea: {
    padding: '20px',
    backgroundColor: 'var(--bg-tertiary)',
    borderTop: '1px solid var(--border-primary)',
  },
  timelineContainer: {
    marginBottom: '16px',
  },
  timeline: {
    position: 'relative',
    height: '40px',
    backgroundColor: 'var(--bg-primary)',
    borderRadius: '8px',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  timelineProgress: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    backgroundColor: 'var(--bg-hover)',
    transition: 'width 0.1s linear',
  },
  timelineHandle: {
    position: 'absolute',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: '16px',
    height: '16px',
    backgroundColor: 'var(--accent-purple)',
    borderRadius: '50%',
    border: '3px solid var(--text-primary)',
    boxShadow: 'var(--shadow-md)',
    zIndex: 10,
  },
  eventMarkers: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  eventMarker: {
    position: 'absolute',
    top: '4px',
    bottom: '4px',
    width: '3px',
    borderRadius: '2px',
    opacity: 0.8,
  },
  timeLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  controls: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
  },
  controlButton: {
    padding: '10px 16px',
    backgroundColor: 'var(--bg-hover)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  playButton: {
    padding: '12px 24px',
    backgroundColor: 'var(--accent-purple)',
    color: '#fff',
    fontSize: '18px',
  },
  speedSelect: {
    padding: '10px 16px',
    backgroundColor: 'var(--bg-hover)',
    border: 'none',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    cursor: 'pointer',
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
    color: 'var(--text-secondary)',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--border-primary)',
    borderTop: '3px solid var(--accent-purple)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Event type colors - using CSS variables for theme compatibility
const EVENT_COLORS = {
  file_edit: 'var(--accent-primary)',
  file_save: 'var(--accent-success)',
  command_executed: 'var(--accent-warning)',
  terminal: 'var(--accent-warning)',
  clipboard: 'var(--accent-purple)',
  idle_start: 'var(--text-muted)',
  idle_end: 'var(--text-muted)',
  capture_start: 'var(--accent-purple)',
  error: 'var(--accent-danger)',
  default: 'var(--text-muted)',
};

// Format timestamp to HH:MM:SS
const formatTime = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Format timestamp for display
const formatTimestamp = (ts) => {
  const date = new Date(ts);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit',
    hour12: false 
  });
};

const ActivityRewind = ({ labId, studentId, studentName, onClose }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenshotIndex, setScreenshotIndex] = useState([]);
  const [activityEvents, setActivityEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentScreenshot, setCurrentScreenshot] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  
  // Refs
  const playbackRef = useRef(null);
  const timelineRef = useRef(null);
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load screenshot index
        const indexData = await screenshotsApi.getIndex(labId, studentId);
        const screenshots = (indexData.screenshots || [])
          .filter(s => s.file)
          .sort((a, b) => a.ts - b.ts);
        setScreenshotIndex(screenshots);
        
        // Load activity events (filter out screenshot internal events)
        try {
          const eventsData = await activityApi.getEvents(labId, studentId, { limit: 1000 });
          const filteredEvents = (eventsData.events || []).filter(event => 
            !['screenshot_request', 'screenshot_failed', 'screenshot_captured'].includes(event.type)
          );
          setActivityEvents(filteredEvents);
        } catch (err) {
          console.warn('Could not load activity events:', err);
        }
        
        if (screenshots.length === 0) {
          setError('No screenshots available for this session');
        }
      } catch (err) {
        console.error('Error loading rewind data:', err);
        setError(err.message || 'Failed to load activity data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [labId, studentId]);
  
  // Current screenshot data
  const currentData = screenshotIndex[currentIndex] || null;
  
  // Timeline calculations
  const timelineStart = screenshotIndex.length > 0 ? screenshotIndex[0].ts : 0;
  const timelineEnd = screenshotIndex.length > 0 ? screenshotIndex[screenshotIndex.length - 1].ts : 0;
  const timelineDuration = timelineEnd - timelineStart;
  const currentTime = currentData ? currentData.ts - timelineStart : 0;
  const progressPercent = timelineDuration > 0 ? (currentTime / timelineDuration) * 100 : 0;
  
  // Load current screenshot image with authentication
  useEffect(() => {
    if (currentData) {
      setImageLoading(true);
      
      // Fetch screenshot with authentication
      screenshotsApi.getImageBlob(labId, studentId, currentData.ts)
        .then(blobUrl => {
          setCurrentScreenshot(blobUrl);
          setImageLoading(false);
        })
        .catch(error => {
          console.error('Failed to load screenshot:', error);
          setCurrentScreenshot(null);
          setImageLoading(false);
        });
    }
    
    // Cleanup: revoke object URL when component unmounts or screenshot changes
    return () => {
      if (currentScreenshot && currentScreenshot.startsWith('blob:')) {
        URL.revokeObjectURL(currentScreenshot);
      }
    };
  }, [currentData, labId, studentId]);
  
  // Playback loop
  useEffect(() => {
    if (isPlaying && screenshotIndex.length > 0) {
      const intervalMs = screenshotIndex.length > 1 
        ? (screenshotIndex[1]?.ts - screenshotIndex[0]?.ts) || 500
        : 500;
      
      playbackRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          if (prev >= screenshotIndex.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, intervalMs / playbackSpeed);
    }
    
    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, screenshotIndex]);
  
  // Handle timeline click
  const handleTimelineClick = useCallback((e) => {
    if (!timelineRef.current || screenshotIndex.length === 0) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickPercent = (e.clientX - rect.left) / rect.width;
    const targetTime = timelineStart + (clickPercent * timelineDuration);
    
    // Find closest screenshot
    let closestIndex = 0;
    let closestDiff = Infinity;
    screenshotIndex.forEach((s, i) => {
      const diff = Math.abs(s.ts - targetTime);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestIndex = i;
      }
    });
    
    setCurrentIndex(closestIndex);
  }, [screenshotIndex, timelineStart, timelineDuration]);
  
  // Jump to event
  const jumpToEvent = useCallback((eventTs) => {
    const closestIndex = screenshotIndex.findIndex(s => s.ts >= eventTs) || 0;
    setCurrentIndex(Math.max(0, closestIndex));
  }, [screenshotIndex]);
  
  // Controls
  const togglePlay = () => setIsPlaying(prev => !prev);
  const stepBack = () => setCurrentIndex(prev => Math.max(0, prev - 1));
  const stepForward = () => setCurrentIndex(prev => Math.min(screenshotIndex.length - 1, prev + 1));
  const jumpToStart = () => setCurrentIndex(0);
  const jumpToEnd = () => setCurrentIndex(screenshotIndex.length - 1);
  
  // Get events near current time
  const nearbyEvents = activityEvents.filter(e => {
    const eventTs = new Date(e.timestamp).getTime();
    return Math.abs(eventTs - (currentData?.ts || 0)) < 5000; // Within 5 seconds
  });
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          stepBack();
          break;
        case 'ArrowRight':
          e.preventDefault();
          stepForward();
          break;
        case 'Home':
          e.preventDefault();
          jumpToStart();
          break;
        case 'End':
          e.preventDefault();
          jumpToEnd();
          break;
        case 'Escape':
          onClose?.();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  
  // Render loading state
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner} />
          <div>Loading activity data...</div>
        </div>
      </div>
    );
  }
  
  return (
    <div style={styles.container}>
      {/* CSS animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.title}>
          <span>📹</span>
          <span>Activity Rewind</span>
          <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>— {studentName || studentId}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={styles.recordingBadge}>
            <div style={styles.recordingDot} />
            <span>{screenshotIndex.length} screenshots</span>
          </div>
          <button 
            style={styles.closeButton}
            onClick={onClose}
            onMouseOver={e => e.target.style.backgroundColor = 'var(--bg-hover)'}
            onMouseOut={e => e.target.style.backgroundColor = 'transparent'}
          >
            Close
          </button>
        </div>
      </div>
      
      {/* Main content */}
      <div style={styles.mainContent}>
        {/* Screenshot display */}
        <div style={styles.screenshotArea}>
          {error ? (
            <div style={styles.noScreenshot}>
              <span style={{ fontSize: '48px' }}>📷</span>
              <span>{error}</span>
            </div>
          ) : imageLoading ? (
            <div style={styles.loading}>
              <div style={styles.spinner} />
            </div>
          ) : currentScreenshot ? (
            <img 
              src={currentScreenshot} 
              alt={`${currentData?.source === 'desktop' ? 'Remote desktop screenshot' : 'Screenshot'} at ${formatTimestamp(currentData?.ts || 0)}`}
              style={styles.screenshot}
            />
          ) : (
            <div style={styles.noScreenshot}>
              <span style={{ fontSize: '48px' }}>📷</span>
              <span>No screenshot available</span>
            </div>
          )}
          
          {/* Timestamp overlay */}
          {currentData && (
            <div style={{
              position: 'absolute',
              bottom: '30px',
              left: '30px',
              padding: '8px 16px',
              backgroundColor: 'rgba(0,0,0,0.7)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'monospace',
            }}>
              {formatTimestamp(currentData.ts)}
              {currentData.source === 'desktop' && (
                <span style={{ marginLeft: 12 }} title="Display label supplied by the viewer; not proof of audit integrity">
                  Remote desktop
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Metadata panel */}
        <div style={styles.metadataPanel}>
          <div style={styles.metadataHeader}>
            Activity Events ({nearbyEvents.length} nearby)
          </div>
          <div style={styles.eventsList}>
            {activityEvents.slice(0, 100).map((event, idx) => {
              const eventTs = new Date(event.timestamp).getTime();
              const isNearby = Math.abs(eventTs - (currentData?.ts || 0)) < 5000;
              const eventType = event.type || event.eventType || 'default';
              const color = EVENT_COLORS[eventType] || EVENT_COLORS.default;
              
              return (
                <div 
                  key={idx}
                  style={{
                    ...styles.eventItem,
                    ...(isNearby ? styles.eventItemActive : {}),
                  }}
                  onClick={() => jumpToEvent(eventTs)}
                >
                  <div style={{ ...styles.eventDot, backgroundColor: color }} />
                  <div style={styles.eventContent}>
                    <div style={styles.eventTime}>{formatTimestamp(eventTs)}</div>
                    <div style={styles.eventText}>
                      {event.type === 'file_edit' && `Edited: ${event.file || 'unknown'}`}
                      {event.type === 'file_save' && `Saved: ${event.file || 'unknown'}`}
                      {event.type === 'command_executed' && `Command: ${event.command?.slice(0, 50) || 'unknown'}`}
                      {event.type === 'clipboard' && `Clipboard: ${event.action || 'copied'}`}
                      {!['file_edit', 'file_save', 'command_executed', 'clipboard'].includes(event.type) && 
                        (event.type || 'Event')}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {activityEvents.length === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                No activity events recorded
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Controls */}
      <div style={styles.controlsArea}>
        {/* Timeline */}
        <div style={styles.timelineContainer}>
          <div 
            ref={timelineRef}
            style={styles.timeline}
            onClick={handleTimelineClick}
          >
            {/* Progress bar */}
            <div style={{ ...styles.timelineProgress, width: `${progressPercent}%` }} />
            
            {/* Event markers */}
            <div style={styles.eventMarkers}>
              {activityEvents.slice(0, 200).map((event, idx) => {
                const eventTs = new Date(event.timestamp).getTime();
                const eventPercent = ((eventTs - timelineStart) / timelineDuration) * 100;
                if (eventPercent < 0 || eventPercent > 100) return null;
                
                const eventType = event.type || 'default';
                const color = EVENT_COLORS[eventType] || EVENT_COLORS.default;
                
                return (
                  <div 
                    key={idx}
                    style={{
                      ...styles.eventMarker,
                      left: `${eventPercent}%`,
                      backgroundColor: color,
                    }}
                  />
                );
              })}
            </div>
            
            {/* Handle */}
            <div style={{ ...styles.timelineHandle, left: `${progressPercent}%` }} />
          </div>
          
          {/* Time labels */}
          <div style={styles.timeLabels}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(timelineDuration)}</span>
          </div>
        </div>
        
        {/* Playback controls */}
        <div style={styles.controls}>
          <button 
            style={styles.controlButton}
            onClick={jumpToStart}
            title="Jump to start (Home)"
          >
            ⏮
          </button>
          <button 
            style={styles.controlButton}
            onClick={stepBack}
            title="Step back (←)"
          >
            ◀
          </button>
          <button 
            style={{ ...styles.controlButton, ...styles.playButton }}
            onClick={togglePlay}
            title="Play/Pause (Space)"
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
          <button 
            style={styles.controlButton}
            onClick={stepForward}
            title="Step forward (→)"
          >
            ▶
          </button>
          <button 
            style={styles.controlButton}
            onClick={jumpToEnd}
            title="Jump to end (End)"
          >
            ⏭
          </button>
          
          <select 
            style={styles.speedSelect}
            value={playbackSpeed}
            onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
          >
            <option value={0.5}>0.5x</option>
            <option value={1}>1x</option>
            <option value={2}>2x</option>
            <option value={4}>4x</option>
            <option value={8}>8x</option>
          </select>
        </div>
        
        {/* Stats */}
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span>📸</span>
            <span>Screenshot {currentIndex + 1} of {screenshotIndex.length}</span>
          </div>
          <div style={styles.statItem}>
            <span>⏱</span>
            <span>Duration: {formatTime(timelineDuration)}</span>
          </div>
          <div style={styles.statItem}>
            <span>🎯</span>
            <span>{nearbyEvents.length} events at this time</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityRewind;

